import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { useApiQuery } from "@/lib/useApiQuery";
import { downloadCsv } from "@/lib/csv";
import { ChevronLeft, ChevronRight, Download, TrendingDown, TrendingUp, Zap } from "lucide-react";
import { useProject } from "@/context/ProjectContext";
import { NoProjectAccess } from "@/components/AppShell";
import { SavingsLeaderboard } from "@/components/SavingsLeaderboard";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const num = (v, d = 0) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

function Kpi({ label, value, unit, testId }) {
  return (
    <div className="k-card p-6" data-testid={testId}>
      <div className="k-sublabel">{label}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-semibold font-heading text-[#156F5B]">{value}</span>
        {unit && <span className="text-xs text-[#6B7280] font-heading">{unit}</span>}
      </div>
    </div>
  );
}

function DeltaBadge({ pct }) {
  if (pct == null) return <span className="k-sublabel">—</span>;
  const down = pct < 0;
  const Icon = down ? TrendingDown : TrendingUp;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-heading font-semibold"
      style={{ color: down ? "#156F5B" : "#B91C1C" }}
    >
      <Icon className="h-3.5 w-3.5" />
      {pct > 0 ? "+" : ""}{pct}%
    </span>
  );
}

export default function AnnualPage() {
  const { project, projects } = useProject();
  const [year, setYear] = useState(new Date().getFullYear());
  const params = useMemo(() => ({ year, ...(project ? { project } : {}) }), [year, project]);
  const { data, loading, err } = useApiQuery(["portfolioYear", params], "/energy/portfolio-year", { params });

  const rows = useMemo(() => (data?.sites || []).filter((r) => r.has_data), [data]);
  const idle = useMemo(() => (data?.sites || []).filter((r) => !r.has_data), [data]);
  const t = data?.totals || {};
  const currency = data?.currency || "CAD";

  if (projects !== undefined && Array.isArray(projects) && projects.length === 0) return <NoProjectAccess />;

  return (
    <div className="max-w-7xl fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label flex items-center gap-2"><Zap className="h-3.5 w-3.5" /> Annual rollup</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]" data-testid="annual-title">
            Portfolio {year}
          </h1>
          <p className="mt-2 text-sm text-[#6B7280]">
            Every site's energy, cost and intensity for the year, with the change on {data?.prev_year ?? year - 1}.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setYear((y) => y - 1)} data-testid="annual-prev-year" className="p-1.5 rounded-md border border-[#E5E7EB] text-[#4D4D4D] hover:border-[#156F5B] hover:text-[#156F5B]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-heading font-semibold text-[#212121] min-w-[60px] text-center" data-testid="annual-year-label">{year}</span>
          <button onClick={() => setYear((y) => Math.min(y + 1, new Date().getFullYear()))} disabled={year >= new Date().getFullYear()} data-testid="annual-next-year" className="p-1.5 rounded-md border border-[#E5E7EB] text-[#4D4D4D] hover:border-[#156F5B] hover:text-[#156F5B] disabled:opacity-40">
            <ChevronRight className="h-4 w-4" />
          </button>
          <Link to="/energy" className="k-btn-outline" data-testid="annual-back-energy">Energy overview</Link>
          <button
            onClick={() =>
              downloadCsv(
                `portfolio-${year}.csv`,
                ["Site", `Electricity ${year} (kWh)`, `Electricity ${data?.prev_year} (kWh)`, "Gas (m³)", `Cost (${currency})`, "EUI (kWh/ft²)", "Change (%)"],
                rows.map((r) => [r.site_dis, r.elec ?? "", r.elec_prev ?? "", r.gas ?? "", r.cost ?? "", r.eui ?? "", r.delta_pct ?? ""])
              )
            }
            disabled={loading || rows.length === 0}
            data-testid="annual-export-btn"
            className="k-btn-outline"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Electricity" value={loading ? "…" : num(t.elec)} unit="kWh" testId="annual-kpi-elec" />
        <Kpi label="Gas" value={loading ? "…" : num(t.gas)} unit="m³" testId="annual-kpi-gas" />
        <Kpi label="Energy cost" value={loading ? "…" : num(t.cost)} unit={currency} testId="annual-kpi-cost" />
        <Kpi label="Sites reporting" value={loading ? "…" : `${t.sites_with_data ?? 0}`} unit={`of ${data?.sites?.length ?? 0}`} testId="annual-kpi-sites" />
      </div>

      {t.saved_kwh != null && (
        <div className="mt-4 k-card p-4 text-sm text-[#212121]" data-testid="annual-savings">
          Across matching months the portfolio used{" "}
          <span className="font-heading font-bold">{num(Math.abs(t.saved_kwh))} kWh</span>{" "}
          {t.saved_kwh > 0 ? "less" : "more"} than {data?.prev_year} — about {num(Math.abs(t.saved_cost || 0))} {currency}.
        </div>
      )}

      {err ? (
        <div className="mt-8 k-card p-6 text-sm text-red-600" data-testid="annual-error">{err}</div>
      ) : (
        <div className="mt-6 k-card overflow-hidden">
          <Table data-testid="annual-table">
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead className="text-right">Electricity (kWh)</TableHead>
                <TableHead className="text-right">Gas (m³)</TableHead>
                <TableHead className="text-right">Cost ({currency})</TableHead>
                <TableHead className="text-right">EUI (kWh/ft²)</TableHead>
                <TableHead className="text-right">vs {data?.prev_year ?? year - 1}</TableHead>
                <TableHead className="text-right">Report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center k-sublabel py-10">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center k-sublabel py-10">No metered sites for {year} yet</TableCell></TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.site_id} data-testid={`annual-row-${r.site_id}`}>
                    <TableCell>
                      <Link to={`/sites/${encodeURIComponent(r.site_id)}/energy`} className="font-heading font-semibold text-[#156F5B] hover:underline">
                        {r.site_dis}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right">{num(r.elec)}</TableCell>
                    <TableCell className="text-right">{num(r.gas)}</TableCell>
                    <TableCell className="text-right">{num(r.cost)}</TableCell>
                    <TableCell className="text-right">{num(r.eui, 3)}</TableCell>
                    <TableCell className="text-right"><DeltaBadge pct={r.delta_pct} /></TableCell>
                    <TableCell className="text-right">
                      <a
                        href={`${API_BASE}/sites/${encodeURIComponent(r.site_id)}/energy/report?start=${year}-01-01&days=366`}
                        target="_blank"
                        rel="noreferrer"
                        className="k-btn-outline inline-flex"
                        data-testid={`annual-report-${r.site_id}`}
                      >
                        <Download className="h-3 w-3" /> PDF
                      </a>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <SavingsLeaderboard sites={data?.sites || []} prevYear={data?.prev_year ?? year - 1} />

      {idle.length > 0 && !loading && (
        <p className="mt-4 k-sublabel" data-testid="annual-idle-note">
          {idle.length} site{idle.length === 1 ? "" : "s"} have no energy meters connected yet.
        </p>
      )}
    </div>
  );
}
